import type { AdminPage } from '@neighbourhood-munchies/shared-types';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loading } from './ui/Loading';

interface RouteGuardProps {
  children: React.ReactNode;
  requiredPage?: AdminPage;
}

/**
 * RouteGuard Component
 *
 * Protects routes by checking authentication status and role-based permissions.
 *
 * Requirements:
 * - 10.1: Check authentication status and redirect to login if unauthenticated
 * - 10.2: Check role-based permissions and redirect to unauthorized page if insufficient
 *
 * @param children - The protected content to render if authorized
 * @param requiredPage - The admin page permission required to access this route
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({ children, requiredPage }) => {
  const { user, staffUser, loading, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user || !staffUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based permissions if a specific page is required
  if (requiredPage && !hasPermission(requiredPage)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // User is authenticated and authorized, render the protected content
  return <>{children}</>;
};
